import React, { MouseEvent, PropsWithChildren, ReactNode, Ref } from 'react';
import clsx from 'clsx';

interface LuiButtonProps extends PlainButtonProps {
  href?: string;
  openInNewTab?: boolean;
  level?: 'text' | 'plain-text' | 'primary' | 'secondary' | 'tertiary';
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
  size?: 'small' | 'med' | 'large';
}

interface PlainButtonProps {
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
  disabled?: boolean;
  style?: React.CSSProperties;
  onClick?: (e: MouseEvent) => void;
  name?: string;
  title?: string;
  'data-testid'?: string;
  className?: string;
  buttonProps?: any;
  ref: Ref<HTMLButtonElement>;
  children: ReactNode;
}

const PlainButton = React.forwardRef<
  HTMLButtonElement,
  PlainButtonProps & { level: string; size: string }
>((props, ref) => {
  return (
    <button
      {...props.buttonProps}
      type={props.type}
      title={props.title}
      disabled={props.disabled}
      name={props.name}
      onClick={props.onClick}
      className={clsx(
        'lui-button',
        `lui-button-${props.level}`,
        `lui-button-${props.size}`,
        props.className
      )}
      data-testid={props['data-testid']}
      style={props.style}
      ref={ref}
    >
      {props.children}
    </button>
  );
});

export const LuiButton = React.forwardRef<
  HTMLButtonElement,
  PropsWithChildren<LuiButtonProps>
>((props, ref) => {
  const _level = props.level ? props.level : 'primary';
  const size = props.size ? props.size : 'med';

  if (props.href) {
    let aProps: any = {
      href: props.href,
      'data-testid': props['data-testid'],
      name: props.name,
      title: props.title,
      className: clsx(
        'lui-button',
        `lui-button-${props.level}`,
        `lui-button-${props.size}`,
        props.className
      ),
      style: props.style,
    };

    if (props.openInNewTab) {
      aProps.target = '_blank';
      aProps.rel = 'noopener noreferrer';
    }

    return <a {...aProps}>{props.children}</a>;
  }

  return (
    <PlainButton
      type={props.type}
      title={props.title}
      disabled={props.disabled}
      name={props.name}
      onClick={props.onClick}
      level={_level}
      size={size}
      data-testid={props['data-testid']}
      style={props.style}
      className={props.className}
      buttonProps={props.buttonProps}
      ref={ref}
    >
      {props.children}
    </PlainButton>
  );
});

export const LuiButtonGroup: React.FC<{ children: any }> = (props) => {
  return (
    <div className="lui-button-group" data-testid={'button-group'}>
      {props.children.map((child: React.FC, i: number) => {
        return (
          <div key={i} className={'lui-button-wrapper'}>
            {child}
          </div>
        );
      })}
    </div>
  );
};
